/*
 * BWT IMAGE CONFIG
 * ----------------
 * All imagery for the site is mapped here so you can swap it later
 * WITHOUT touching any component code.
 *
 * CLOUDINARY (recommended for production):
 *   1. Set CLOUDINARY_CLOUD_NAME to your Cloudinary cloud name.
 *   2. Upload your images to Cloudinary and note each public ID.
 *   3. Replace any URL below with just the public ID string, e.g.
 *          hero: "bwt/home-hero"
 *      The `img()` helper automatically builds an optimised, responsive
 *      Cloudinary URL (f_auto, q_auto, responsive width, smart crop).
 *
 * Any value that already starts with "http" is used as-is (current stock
 * imagery). So you can migrate one image at a time.
 */

export const CLOUDINARY_CLOUD_NAME = "nlf7qoje";

// Build an optimised Cloudinary delivery URL from a public ID.
export function cloudinary(publicId, { w = 1400, ar, crop = "fill" } = {}) {
  const t = ["f_auto", "q_auto", `w_${w}`, `c_${crop}`, crop === "fill" ? "g_auto" : null, ar ? `ar_${ar}` : null]
    .filter(Boolean)
    .join(",");
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/${t}/${publicId}`;
}

// Resolve an image reference: full URL -> as-is; otherwise treat as a Cloudinary public ID.
export function img(ref, opts) {
  if (!ref) return "";
  if (typeof ref === "string" && ref.startsWith("http")) return ref;
  return cloudinary(ref, opts);
}

const U = (id, q = "") =>
  `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400${q}`;

// ---- raw sources (swap these for Cloudinary public IDs later) ----
const SRC = {
  ship1: U("1769144256181-698b8f807066"),
  shipSunset: "https://images.pexels.com/photos/262353/pexels-photo-262353.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1400",
  containers: "https://images.pexels.com/photos/163726/belgium-antwerp-shipping-container-163726.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1400",
  loom1: U("1675176785803-bffbbb0cd2f4"),
  loom2: U("1636986056375-184676d8ca14"),
  yarn1: U("1517146783983-418c681b56c5"),
  yarn2: U("1542044801-30d3e45ae49a"),
  heritage1: "https://images.pexels.com/photos/32673642/pexels-photo-32673642.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1400",
  heritage2: U("1640292343595-889db1c8262e"),
  // towels
  whiteStack: U("1760722974657-f64bce2f9cc5"),
  grayFolded: "https://images.pexels.com/photos/4210378/pexels-photo-4210378.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1400",
  linen: U("1770731206301-43a9683f3438"),
  creamBasket: U("1764981126598-ab224bd71273"),
  rolledNeutral: "https://images.pexels.com/photos/45980/pexels-photo-45980.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1400",
  beigeRolled: "https://images.pexels.com/photos/2672634/pexels-photo-2672634.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1400",
  beachSand: U("1718839932371-7adaf5edc96a"),
  striped: U("1550912788-1a59d63dbd3d"),
  colorStack: U("1625931046289-e51edea3e176"),
  colorPile: U("1638598125618-6095d93581a9"),
  poolside: "https://images.pexels.com/photos/11021165/pexels-photo-11021165.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1400",
  poolColor: "https://images.pexels.com/photos/33811116/pexels-photo-33811116.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1400",
  hotelBed: U("1666934209606-a955a12edd63"),
  greenTowels: U("1523471826770-c437b4636fe6"),
  soapTowel: U("1631015108968-ba3b87f89005"),
  shelfTowels: U("1617811449482-31093c8cee16"),
  // hero + new categories
  towelHero: "https://images.pexels.com/photos/6188050/pexels-photo-6188050.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600",
  robe1: "https://images.pexels.com/photos/10130830/pexels-photo-10130830.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1400",
  robe2: "https://images.pexels.com/photos/22598409/pexels-photo-22598409.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1400",
  floormat1: U("1584069793904-574c37c3316f"),
  rug: U("1580229080435-1c7e2ce835c1"),
  floormat2: "https://images.pexels.com/photos/34667257/pexels-photo-34667257.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1400",
};

// Build an optimised Cloudinary URL from a public ID.
const cld = (id) => cloudinary(id, { w: 1400 });
const cldProduct = (id) => cloudinary(id, { w: 1400, crop: "fit" });

/*
 * ====================================================================
 *  EDIT HERE — add your Cloudinary public IDs per product category.
 *  hero  = 1 main image (public ID string)
 *  gallery = up to 5 more image public IDs
 *  Leave hero as "" and gallery as [] to keep the stock fallback image.
 *  Cloud name is set in CLOUDINARY_CLOUD_NAME above (nlf7qoje).
 * ====================================================================
 */
const CATEGORY_IDS = {
  bath:      { hero: "bath_3", gallery: ["bath_1", "bath_2", "bath_4", "bath_5"] },
  hand:      { hero: "hand_1", gallery: ["hand_2", "hand_3","hand_4",] },
  face:      { hero: "face_2", gallery: ["face_1","face_3","face_4","face_5",] },
  pool:      { hero: "pool_1", gallery: ["pool_2","pool_3","pool_4",] },
  beach:     { hero: "beach_1", gallery: ["beach_2","beach_3","beach_4","beach_5","beach_6"] },
  kitchen:   { hero: "kitchen_1", gallery: ["kitchen_2","kitchen_3","kitchen_4","kitchen_5",] },
  mats:      { hero: "bath_mat_n1", gallery: ["bath_mat_n2","bath_mat_3","bath_mat_4","bath_mat_5",] },
  jacquard:  { hero: "jackquard_1", gallery: ["jackquard_2","jackquard_3","jackquard_4","jackquard_5",] },
  robes:     { hero: "bath_rob_2", gallery: ["bath_rob_1","bath_rob_3","bath_rob_4",] },
  floormats: { hero: "satranji_1", gallery: ["satranji", "satranji_2"] },
  bamboo:    { hero: "bamboo_1", gallery: ["bamboo_2","bamboo_3","bamboo_4","bamboo_5"] },
  chaddar:   { hero: "chaddar_1", gallery: ["chaddar_2","chaddar_3","chaddar_4"] },
};

// Stock fallbacks used until you add Cloudinary IDs above.
const STOCK = {
  bath: { hero: SRC.whiteStack, gallery: [SRC.rolledNeutral, SRC.creamBasket, SRC.beigeRolled, SRC.linen] },
  hand: { hero: SRC.soapTowel, gallery: [SRC.grayFolded, SRC.shelfTowels, SRC.hotelBed, SRC.rolledNeutral] },
  face: { hero: SRC.shelfTowels, gallery: [SRC.whiteStack, SRC.linen, SRC.creamBasket, SRC.beigeRolled] },
  pool: { hero: SRC.poolside, gallery: [SRC.poolColor, SRC.beachSand, SRC.striped, SRC.colorStack] },
  beach: { hero: SRC.beachSand, gallery: [SRC.striped, SRC.colorStack, SRC.colorPile, SRC.poolColor] },
  kitchen: { hero: SRC.linen, gallery: [SRC.rolledNeutral, SRC.creamBasket, SRC.beigeRolled, SRC.grayFolded] },
  mats: { hero: SRC.beigeRolled, gallery: [SRC.rolledNeutral, SRC.creamBasket, SRC.linen, SRC.grayFolded] },
  jacquard: { hero: SRC.greenTowels, gallery: [SRC.striped, SRC.colorStack, SRC.colorPile, SRC.shelfTowels] },
  robes: { hero: SRC.robe1, gallery: [SRC.robe2, SRC.hotelBed, SRC.soapTowel, SRC.shelfTowels] },
  floormats: { hero: SRC.floormat1, gallery: [SRC.floormat2, SRC.rug, SRC.beigeRolled, SRC.rolledNeutral] },
  bamboo: { hero: SRC.greenTowels, gallery: [SRC.whiteStack, SRC.linen, SRC.creamBasket, SRC.rolledNeutral] },
  chaddar: { hero: SRC.linen, gallery: [SRC.creamBasket, SRC.beigeRolled, SRC.grayFolded, SRC.rolledNeutral] },
};

function buildCategory(key) {
  const ids = CATEGORY_IDS[key] || {};
  const fb = STOCK[key];
  return {
    hero: ids.hero ? cldProduct(ids.hero) : fb.hero,
    gallery: ids.gallery && ids.gallery.length ? ids.gallery.map(cldProduct) : fb.gallery,
  };
}

const categories = Object.keys(CATEGORY_IDS).reduce((acc, k) => {
  acc[k] = buildCategory(k);
  return acc;
}, {});

export const IMAGES = {
  home: {
    hero: cld("hero"),
    trade: {
      export: SRC.containers,
      domestic: SRC.whiteStack,
      sourcing: SRC.yarn1,
      partnership: SRC.shipSunset,
    },
    why: SRC.loom1,
    cta: SRC.shipSunset,
  },
  about: {
    hero: SRC.loom2,
    // legacy images shown below the About timeline (your Cloudinary uploads)
    legacy1: cld("WhatsApp_Image_2026-08-15_at_3.50.45_PM"),
    legacy2: cld("WhatsApp_Image_2026-08-15_at_3.50.45_PM_1"),
    loom: SRC.loom1,
  },
  products: {
    hero: SRC.whiteStack,
    yarn: cld("yarn_options"),
    manufacturing: SRC.loom1,
    applications: SRC.hotelBed,
    export: SRC.containers,
  },
  categories,
};
